/**
 * 热点雷达 · v2
 * - FilterBar(题材 · 频道 · 排序 · 含已屏蔽)
 * - Tab 推荐 / 已屏蔽
 * - RadarCard(IP-ized 信息)+ IpSeedDrawer 详情抽屉
 * - 未 IP 化置顶横幅 + 批量 IP 化抽屉
 * - 手动创建入口：选择题材 + 输入灵感 → AI 流式生成 IP Seed
 */
import { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Flame, AlertTriangle, RefreshCw, Settings2, Wand2, Dices, X, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { api, streamSse } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { GENRES, GENRE_META, type Genre } from '@starfox/shared-types';
import type { TopicCard } from '@starfox/shared-types';
import { FilterBar, DEFAULT_FILTERS, type RadarFilters } from './FilterBar';
import { RadarCard } from './RadarCard';
import { IpSeedDrawer } from './IpSeedDrawer';
import { IpizeDrawer } from './IpizeDrawer';
import { ProjectsHall } from './ProjectsHall';

interface TopicsResponse {
  topics: TopicCard[];
  banned: TopicCard[];
  count: number;
  banned_count: number;
  seeded?: boolean;
}

interface RunResult {
  ok: boolean;
  blocked: boolean;
  warning: boolean;
  reason: string;
  topics: TopicCard[];
  banned?: TopicCard[];
  count?: number;
  banned_count?: number;
  safety?: { used: number; limit: number; softWarnAt: number };
}

function buildSearchParams(f: RadarFilters): URLSearchParams {
  const sp = new URLSearchParams();
  f.genres.forEach((g) => sp.append('genres', g));
  f.audiences.forEach((a) => sp.append('audiences', a));
  sp.set('sort', f.sort);
  if (f.includeBanned) sp.set('include_banned', 'true');
  sp.set('limit', '60');
  return sp;
}

async function fetchTopics(f: RadarFilters): Promise<TopicsResponse> {
  try {
    const sp = buildSearchParams(f);
    return await api.get('radar/topics', { searchParams: sp }).json<TopicsResponse>();
  } catch {
    return { topics: [], banned: [], count: 0, banned_count: 0 };
  }
}

export function RadarPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'SUPER_ADMIN' || role === 'TENANT_ADMIN';
  const [filters, setFilters] = useState<RadarFilters>(DEFAULT_FILTERS);
  const [activeTab, setActiveTab] = useState<'main' | 'banned'>('main');
  const [drawerTopic, setDrawerTopic] = useState<TopicCard | null>(null);
  const [ipizeOpen, setIpizeOpen] = useState(false);
  const [genDialogOpen, setGenDialogOpen] = useState(false);

  const { data, isLoading, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['radar', 'topics', filters],
    queryFn: () => fetchTopics(filters),
    placeholderData: (prev) => prev,
    // 主榜里只要还有 ipizing=true 的卡,就 5s 轮询追后端进度;否则 60s 心跳
    refetchInterval: (q) => {
      const d = q.state.data as TopicsResponse | undefined;
      const all = (d?.topics ?? []).concat(d?.banned ?? []);
      return all.some((t) => t.ipizing) ? 5_000 : 60_000;
    },
  });

  const { data: safety } = useQuery<{
    used: number;
    limit: number;
    warning: boolean;
    blocked: boolean;
    reason: string;
    lastRunAt: string | null;
  }>({
    queryKey: ['crawler', 'safety'],
    queryFn: () => api.get('crawler/safety').json(),
    refetchInterval: 60_000,
  });

  const run = useMutation<RunResult, Error, boolean>({
    mutationFn: async (force) => {
      const res = await api
        .post('crawler/run', {
          searchParams: force ? { force: 'true' } : {},
          throwHttpErrors: false,
        })
        .json<RunResult>();
      return res;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['crawler', 'safety'] });
      if (res?.ok) {
        qc.invalidateQueries({ queryKey: ['radar', 'topics'] });
        const n = res.count ?? res.topics?.length ?? 0;
        toast.success('抓取完成', `新增 ${n} 条主榜热点 · 屏蔽 ${res.banned_count ?? 0} 条`);
      } else if (res?.blocked) {
        toast.error('已阻断', res.reason || '当前已达安全阈值');
      } else if (res?.warning) {
        toast.warning('已接近阈值', res.reason || '请谨慎继续');
      }
    },
    onError: (err: Error) => {
      toast.error('抓取失败', err.message || '网络异常,稍后重试');
    },
  });

  const kickoffMutation = useMutation({
    mutationFn: async (topic: TopicCard) => {
      const tpl = topic.kickoffTemplates?.[0];
      const res = await api
        .post('radar/kickoff', {
          json: { topicId: topic.topicId, templateId: tpl?.templateId },
        })
        .json<{ projectId: string; episodeId: string }>();
      return res;
    },
    onSuccess: (res) => {
      navigate({
        to: '/writer/$episodeId' as never,
        params: { episodeId: res.episodeId } as never,
      });
    },
  });

  const ipizeMutation = useMutation({
    mutationFn: async (topicIds: string[]) => {
      const res = await api
        .post('radar/ipize', {
          json: { topicIds, force: true },
          timeout: 120_000,
        })
        .json<{ ok: boolean; updated: TopicCard[]; failed: string[]; count: number }>();
      return res;
    },
    onMutate: (ids) => {
      // 乐观更新:把这一批 topic 立刻标记 ipizing=true,避免等下一次轮询
      qc.setQueriesData({ queryKey: ['radar', 'topics'] }, (old: TopicsResponse | undefined) => {
        if (!old) return old;
        const mark = (t: TopicCard) => (ids.includes(t.topicId) ? { ...t, ipizing: true } : t);
        return { ...old, topics: old.topics.map(mark), banned: old.banned.map(mark) };
      });
    },
    onSuccess: (res) => {
      const ok = res?.count ?? res?.updated?.length ?? 0;
      const failed = res?.failed?.length ?? 0;
      if (failed === 0) {
        toast.success('IP 化完成', `已为 ${ok} 条热点生成 IP Seed`);
      } else if (ok === 0) {
        toast.error('IP 化失败', `全部 ${failed} 条失败,请检查 LLM 配置或稍后重试`);
      } else {
        toast.warning('部分 IP 化失败', `成功 ${ok} 条 · 失败 ${failed} 条(可重试)`);
      }
    },
    onError: (err: Error) => {
      toast.error('IP 化失败', err.message || '网络异常,稍后重试');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['radar', 'topics'] });
    },
  });

  const handleKickoff = (t: TopicCard) => kickoffMutation.mutate(t);
  const handleOpenDetail = (t: TopicCard) => setDrawerTopic(t);
  const handleIpize = (t: TopicCard) => ipizeMutation.mutate([t.topicId]);
  const handleBatchIpize = (ids: string[]) => ipizeMutation.mutate(ids);

  const allTopics = data?.topics ?? [];
  const banned = data?.banned ?? [];
  const ipizingIds = useMemo(
    () => new Set(allTopics.concat(banned).filter((t) => !!t.ipizing).map((t) => t.topicId)),
    [allTopics, banned],
  );
  // 主榜只展示已 IP 化的卡片;未 IP 化的统一走「待 IP 化」抽屉
  const topics = useMemo(() => allTopics.filter((t) => !!t.ipSeed?.ipName), [allTopics]);
  const pendingIpize = useMemo(
    () => allTopics.filter((t) => !t.ipSeed?.ipName),
    [allTopics],
  );
  const counts = useMemo(
    () => ({ topics: topics.length, banned: data?.banned_count ?? 0 }),
    [topics, data],
  );

  // 切换 includeBanned 时,如果关掉就回主榜
  const onFilterChange = (next: RadarFilters) => {
    setFilters(next);
    if (!next.includeBanned && activeTab === 'banned') setActiveTab('main');
  };

  // 最后一次主榜更新时间(本地相对时长,每 30s 重渲一次)
  const [, forceTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceTick((v) => v + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  const lastPulledLabel = useMemo(() => {
    const ts = safety?.lastRunAt ? new Date(safety.lastRunAt).getTime() : dataUpdatedAt;
    if (!ts) return '尚未拉取';
    const diff = Math.max(0, Date.now() - ts);
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec} 秒前更新`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} 分钟前更新`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} 小时前更新`;
    return `${Math.floor(hr / 24)} 天前更新`;
  }, [safety?.lastRunAt, dataUpdatedAt]);

  return (
    <div className="space-y-5 p-6 lg:p-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Flame className="h-4 w-4 text-warning" />
            <span>三段式流水线 · CLEAN → CURATE → IP-IZE · 多源去重</span>
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight">热点雷达</h1>
          <p className="mt-1 text-sm text-neutral-600">
            10 大漫剧题材 · Top 自动 IP 化 · 已屏蔽题目独立可查
          </p>
        </div>
        <div className="flex items-center gap-2">
          {safety && (
            <Badge variant={safety.blocked ? 'danger' : safety.warning ? 'warning' : 'success'}>
              {safety.used}/{safety.limit}
            </Badge>
          )}
          <span className="hidden text-xs text-neutral-500 sm:inline">{lastPulledLabel}</span>
          {isAdmin && (
            <Link to="/admin/scripts/radar-ipize">
              <Button variant="ghost" size="sm">
                <Settings2 className="h-4 w-4" />
                配置
              </Button>
            </Link>
          )}
          <Button
            variant="agent"
            agentColor="radar"
            size="sm"
            onClick={() => setGenDialogOpen(true)}
          >
            <Dices className="h-4 w-4" />
            抽奖灵感
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              run.mutate(false, { onSettled: () => refetch() });
            }}
            disabled={run.isPending || isFetching || (safety?.blocked ?? false)}
            title={safety?.blocked ? safety.reason : '触发一次抓取并刷新榜单'}
          >
            <RefreshCw
              className={
                run.isPending || isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'
              }
            />
            手动拉取
          </Button>
        </div>
      </header>

      {safety?.warning && !safety.blocked && (
        <div className="flex items-start gap-2 rounded-md bg-warning/10 p-3 text-xs text-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>{safety.reason || '当日抓取已接近安全阈值,请谨慎继续。'}</div>
        </div>
      )}

      {data?.seeded && (
        <div className="rounded-md bg-info-soft p-3 text-xs text-info">
          当前展示的是兜底种子数据(数据库还没有抓取结果),点击「手动拉取」生成真实热点。
        </div>
      )}

      <FilterBar value={filters} onChange={onFilterChange} counts={counts} />

      {pendingIpize.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-info/30 bg-info-soft/40 px-3 py-2 text-xs">
          <div className="flex items-center gap-2 text-info">
            <Wand2 className="h-4 w-4" />
            <span>
              当前主榜有 <strong>{pendingIpize.length}</strong> 条 <strong>未 IP 化</strong>
              的热点(LLM 打标失败或新增热点),建议批量重跑生成完整 IP 包。
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIpizeOpen(true)}
            >
              查看 / 选择
            </Button>
            <Button
              size="sm"
              variant="agent"
              agentColor="radar"
              onClick={() => handleBatchIpize(pendingIpize.map((t) => t.topicId))}
              disabled={ipizeMutation.isPending}
            >
              <Wand2 className={ipizeMutation.isPending ? 'h-3.5 w-3.5 animate-pulse' : 'h-3.5 w-3.5'} />
              一键全部 IP 化
            </Button>
          </div>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'main' | 'banned')}
        className="space-y-3"
      >
        <TabsList>
          <TabsTrigger value="main">
            推荐主榜 <span className="ml-1 text-xs text-neutral-500">{counts.topics}</span>
          </TabsTrigger>
          <TabsTrigger value="banned" disabled={!filters.includeBanned}>
            已屏蔽 <span className="ml-1 text-xs text-warning">{counts.banned}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="main">
          <CardGrid
            cards={topics}
            isLoading={isLoading}
            empty="未匹配到符合筛选条件的题目,尝试调整题材/人群,或点击「手动拉取」。"
            onOpenDetail={handleOpenDetail}
            onKickoff={handleKickoff}
            onIpize={handleIpize}
          />
        </TabsContent>

        <TabsContent value="banned">
          <CardGrid
            cards={banned}
            isLoading={isLoading}
            empty="当前没有已屏蔽题目。"
            onOpenDetail={handleOpenDetail}
          />
        </TabsContent>
      </Tabs>

      <IpSeedDrawer
        open={!!drawerTopic}
        topic={drawerTopic ? topics.concat(banned).find((t) => t.topicId === drawerTopic.topicId) ?? drawerTopic : null}
        onClose={() => setDrawerTopic(null)}
        onKickoff={(t) => {
          setDrawerTopic(null);
          handleKickoff(t);
        }}
        onIpize={handleIpize}
      />

      <IpizeDrawer
        open={ipizeOpen}
        topics={pendingIpize}
        ipizingIds={ipizingIds}
        onClose={() => setIpizeOpen(false)}
        onIpize={handleBatchIpize}
      />

      <GenerateSeedDialog
        open={genDialogOpen}
        onClose={() => setGenDialogOpen(false)}
        onAdded={() => setGenDialogOpen(false)}
      />

      <ProjectsHall />
    </div>
  );
}

function CardGrid({
  cards,
  isLoading,
  empty,
  onOpenDetail,
  onKickoff,
  onIpize,
}: {
  cards: TopicCard[];
  isLoading: boolean;
  empty: string;
  onOpenDetail?: (t: TopicCard) => void;
  onKickoff?: (t: TopicCard) => void;
  onIpize?: (t: TopicCard) => void;
}) {
  if (isLoading) {
    return (
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-72 animate-pulse rounded-lg bg-surface-variant" />
        ))}
      </section>
    );
  }
  if (cards.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-outline-variant p-12 text-center text-sm text-neutral-500">
        {empty}
      </div>
    );
  }
  return (
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {cards.map((t) => (
        <RadarCard
          key={t.topicId}
          topic={t}
          onOpenDetail={onOpenDetail}
          onKickoff={onKickoff}
          onIpize={onIpize}
          ipizing={!!t.ipizing}
        />
      ))}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 手动抽奖灵感对话框
// ─────────────────────────────────────────────────────────────────────

interface GeneratedIpSeedCharacter {
  name: string;
  role: string;
  identity: string;
  gender?: string;
  age?: string;
  appearance?: string;
  traits?: string[];
  desire?: string;
  fear?: string;
  flaw?: string;
  speakingStyle?: string;
  habitual?: string;
  relation: string;
}

interface GeneratedIpSeed {
  ipName: string;
  hookQuestion: string;
  loglinePinned: string;
  coreConflict: string;
  mcArchetype: string;
  antagonistArchetype: string;
  cheerPoints: string[];
  openingScene15s: string;
  characters?: GeneratedIpSeedCharacter[];
  storyline: string;
  estimatedCpm: number;
  locale?: { language?: string; region?: string };
}

const LOCALE_OPTIONS = [
  {
    key: 'zh-CN',
    label: '简体中文',
    regionLabel: '中国大陆',
    payload: { language: 'zh-CN', region: 'CN' },
    hint: '按中国大陆短剧语境生成',
  },
  {
    key: 'en-US',
    label: 'English',
    regionLabel: 'North America',
    payload: { language: 'en', region: 'US' },
    hint: '剧情、角色、台词按北美本地化重写',
  },
] as const;

type LocaleKey = (typeof LOCALE_OPTIONS)[number]['key'];

function GenerateSeedDialog({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const qc = useQueryClient();
  const [selectedGenre, setSelectedGenre] = useState<Genre | ''>('');
  const [selectedLocale, setSelectedLocale] = useState<LocaleKey>('zh-CN');
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>([]);
  const [episodeCount, setEpisodeCount] = useState<number>(36);
  const [episodeDuration, setEpisodeDuration] = useState<number>(3);
  const [hint, setHint] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [result, setResult] = useState<GeneratedIpSeed | null>(null);
  const [parseError, setParseError] = useState(false);
  const [saving, setSaving] = useState(false);
  const ctrlRef = useRef<AbortController | null>(null);

  const toggleAud = (a: string) => {
    setSelectedAudiences((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a],
    );
  };

  function reset() {
    setSelectedGenre('');
    setSelectedLocale('zh-CN');
    setSelectedAudiences([]);
    setEpisodeCount(36);
    setEpisodeDuration(3);
    setHint('');
    setStreaming(false);
    setStreamText('');
    setResult(null);
    setParseError(false);
    ctrlRef.current?.abort();
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleGenerate() {
    if (!selectedGenre || streaming) return;
    setResult(null);
    setParseError(false);
    setStreamText('');
    setStreaming(true);

    let accumulated = '';
    ctrlRef.current = streamSse<{ chunk?: string; done?: boolean; ipSeed?: GeneratedIpSeed | null; error?: string; raw?: string; parseError?: boolean }>(
      'radar/generate-seed',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          genreKey: selectedGenre,
          audiences: selectedAudiences,
          episodeCount: episodeCount,
          episodeDuration: episodeDuration,
          userHint: hint.trim() || undefined,
          locale: LOCALE_OPTIONS.find((option) => option.key === selectedLocale)?.payload,
        }),
      },
      (data) => {
        if (data.chunk) {
          accumulated += data.chunk;
          setStreamText(accumulated);
        }
        if (data.done) {
          if (data.ipSeed) {
            setResult(data.ipSeed);
          } else if (data.parseError && data.raw) {
            setParseError(true);
            toast.error('AI 返回格式异常', '已保留生成文本，请重试');
          }
          setStreaming(false);
        }
        if (data.error) {
          toast.error('生成失败', data.error);
          setStreaming(false);
        }
      },
      (err) => {
        if ((err as Error)?.name !== 'AbortError') {
          toast.error('生成失败', String(err).slice(0, 200));
        }
        setStreaming(false);
      },
      () => setStreaming(false),
    );
  }

  async function handleConfirm() {
    if (!result || !selectedGenre || saving) return;
    setSaving(true);
    try {
      await api
        .post('radar/topics/create', {
          json: {
            ipSeed: result,
            genres: [selectedGenre],
            audiences: selectedAudiences,
          },
        })
        .json();
      await qc.invalidateQueries({ queryKey: ['radar', 'topics'] });
      toast.success('已加入主榜', `《${result.ipName}》已添加到热点雷达`);
      reset();
      onAdded();
    } catch (e) {
      toast.error('保存失败', String(e).slice(0, 200));
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div className="relative flex w-full max-w-2xl flex-col rounded-xl border border-outline-variant bg-surface shadow-2xl"
           style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        {/* 顶栏 - 固定 */}
        <div className="flex shrink-0 items-center justify-between border-b border-outline-variant px-5 py-4">
          <div className="flex items-center gap-2">
            <Dices className="h-5 w-5 text-brand-500" />
            <h2 className="text-base font-semibold">抽奖灵感</h2>
            <span className="text-xs text-neutral-500">· AI 为你随机生成一个漫剧 IP 包</span>
          </div>
          <button type="button" onClick={handleClose} className="rounded p-1 hover:bg-surface-variant">
            <X className="h-4 w-4 text-neutral-500" />
          </button>
        </div>

        {/* 滚动内容区 */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 p-5">
            {/* 题材选择 */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-neutral-600">选择题材（必选）</label>
              <div className="grid grid-cols-3 gap-2">
                {GENRES.map((g) => {
                  const meta = GENRE_META.find((m) => m.key === g);
                  return (
                    <button
                      type="button"
                      key={g}
                      onClick={() => setSelectedGenre(g)}
                      className={`rounded-lg border p-2.5 text-left transition ${
                        selectedGenre === g
                          ? 'border-brand-500 bg-brand-500/10 text-brand-600'
                          : 'border-outline-variant text-neutral-700 hover:bg-surface-variant'
                      }`}
                    >
                      <div className="text-xs font-medium">{g}</div>
                      {meta && (
                        <div className="mt-0.5 text-[10px] leading-tight text-neutral-500 line-clamp-2">
                          {meta.tagline}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-neutral-600">语言 / 地区</label>
              <div className="grid gap-2 sm:grid-cols-2">
                {LOCALE_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.key}
                    onClick={() => setSelectedLocale(option.key)}
                    disabled={streaming}
                    className={`rounded-lg border p-2.5 text-left transition ${
                      selectedLocale === option.key
                        ? 'border-brand-500 bg-brand-500/10 text-brand-600'
                        : 'border-outline-variant text-neutral-700 hover:bg-surface-variant'
                    }`}
                  >
                    <div className="text-xs font-semibold">{option.label} · {option.regionLabel}</div>
                    <div className="mt-0.5 text-[10px] leading-tight text-neutral-500">{option.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* 人群选择（可多选） */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-neutral-600">人群频道（可多选，不选则男女通吃）</label>
              <div className="flex gap-2">
                {(['女频', '男频'] as const).map((a) => (
                  <button
                    type="button"
                    key={a}
                    onClick={() => toggleAud(a)}
                    className={`rounded-lg border px-4 py-1.5 text-sm transition ${
                      selectedAudiences.includes(a)
                        ? 'border-brand-500 bg-brand-500/10 text-brand-600'
                        : 'border-outline-variant text-neutral-700 hover:bg-surface-variant'
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-neutral-600">
                集数体量 <span className="text-neutral-400 font-normal">· AI 按此体量规划分集大纲和钩子节奏</span>
              </label>
              {([
                { label: '轻量', hint: '~1-3 小时', values: [24, 30, 36] },
                { label: '中等', hint: '~2-6 小时', values: [48, 60, 72] },
                { label: '长篇', hint: '~3-8 小时', values: [84, 99] },
              ] as const).map((tier) => (
                <div key={tier.label} className="flex items-center gap-2">
                  <span className="w-10 shrink-0 text-[11px] text-neutral-400">{tier.label}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {tier.values.map((n) => (
                      <button
                        type="button"
                        key={n}
                        onClick={() => setEpisodeCount(n)}
                        disabled={streaming}
                        className={`rounded-full border px-3 py-0.5 text-xs transition ${
                          episodeCount === n
                            ? 'border-brand-500 bg-brand-500/10 text-brand-600 font-semibold'
                            : 'border-outline-variant text-neutral-700 hover:border-brand-400 hover:text-brand-600'
                        }`}
                      >
                        {n} 集
                      </button>
                    ))}
                  </div>
                  <span className="text-[10px] text-neutral-400">{tier.hint}</span>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-neutral-600">
                每集剧情长度
              </label>
              <div className="flex gap-2">
                {([3, 5, 10] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setEpisodeDuration(d)}
                    disabled={streaming}
                    className={`rounded-full border px-3 py-0.5 text-xs transition ${
                      episodeDuration === d
                        ? 'border-brand-500 bg-brand-500/10 text-brand-600 font-semibold'
                        : 'border-outline-variant text-neutral-700 hover:border-brand-400 hover:text-brand-600'
                    }`}
                  >
                    {d} 分钟
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-neutral-400">
                {episodeDuration === 3 ? '分集大纲 · 每集约 150-250 字（主要内容+冲突推进+钩子）'
                  : episodeDuration === 5 ? '分集大纲 · 每集约 300-450 字（主要内容+冲突推进+钩子）'
                  : '分集大纲 · 每集约 500-750 字（主要内容+冲突推进+钩子）'}
              </p>
            </div>

            {/* 灵感输入（可选） */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-neutral-600">
                你的想法（可选，AI 会参考）
              </label>
              <textarea
                className="w-full resize-none rounded-md border border-outline-variant bg-surface-variant/20 px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-brand-300 focus:outline-none focus:ring-1 focus:ring-brand-200"
                rows={2}
                placeholder="例如：女主是个外卖员，穿越到修真界，想做物流生意……"
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                disabled={streaming}
              />
            </div>

            {/* 生成按钮 */}
            <div className="flex items-center gap-2">
              <Button
                variant="agent"
                agentColor="radar"
                onClick={handleGenerate}
                disabled={!selectedGenre || streaming}
                className="flex-1"
              >
                {streaming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Dices className="h-4 w-4" />
                )}
                {streaming ? 'AI 正在抽奖…' : '抽奖'}
              </Button>
              {result && (
                <Button variant="secondary" onClick={reset}>
                  重新抽
                </Button>
              )}
            </div>

            {/* 生成中状态 */}
            {streaming && !result && (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-outline-variant bg-surface-variant/20 py-8 text-sm text-neutral-500">
                <Loader2 className="h-6 w-6 animate-spin text-brand-400" />
                <span>AI 正在创作 IP 包，请稍候…</span>
                <span className="text-xs text-neutral-400">已生成 {streamText.length} 字</span>
              </div>
            )}

            {!streaming && !result && parseError && streamText && (
              <div className="space-y-3 rounded-lg border border-warning/30 bg-warning-soft/20 p-4">
                <div className="flex items-center gap-2 text-sm font-medium text-warning">
                  <AlertTriangle className="h-4 w-4" />
                  AI 生成了内容但格式解析失败，请重试
                </div>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-surface-variant/40 p-3 text-xs text-neutral-600">
                  {streamText.slice(0, 2000)}
                </pre>
              </div>
            )}

            {/* 结果卡 */}
            {result && (
              <div className="space-y-3 rounded-lg border border-brand-200 bg-brand-50/30 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-bold text-neutral-800">{result.ipName}</h3>
                    {result.hookQuestion && (
                      <p className="mt-1 text-sm font-medium text-brand-600">{result.hookQuestion}</p>
                    )}
                    <p className="mt-1 text-sm text-neutral-600">{result.loglinePinned}</p>
                  </div>
                  <Badge variant="info" className="shrink-0 text-xs">
                    CPM {result.estimatedCpm}
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="font-medium text-neutral-500">核心冲突</span>
                      <p className="mt-0.5 text-neutral-700">{result.coreConflict}</p>
                    </div>
                    <div>
                      <span className="font-medium text-neutral-500">主角原型</span>
                      <p className="mt-0.5 text-neutral-700">{result.mcArchetype}</p>
                    </div>
                  </div>

                  {result.cheerPoints?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {result.cheerPoints.map((p) => (
                        <span key={p} className="rounded-full bg-brand-100 px-2 py-0.5 text-[11px] text-brand-700">
                          {p}
                        </span>
                      ))}
                    </div>
                  )}

                  {result.characters && result.characters.length > 0 && (
                    <div className="text-xs">
                      <div className="mb-2 font-medium text-neutral-600">出场人物</div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {result.characters.map((c) => (
                          <div key={c.name} className="rounded-md border border-outline-variant/30 bg-surface p-2.5">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-neutral-800">{c.name}</span>
                              <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] text-brand-600">
                                {c.role}
                              </span>
                              {c.gender && (
                                <span className="text-[10px] text-neutral-400">{c.gender}{c.age ? `·${c.age}` : ''}</span>
                              )}
                            </div>
                            <p className="mt-1 text-neutral-500">{c.identity}</p>
                            {c.traits && c.traits.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {c.traits.map((t) => (
                                  <span key={t} className="rounded-full bg-surface-variant/60 px-1.5 py-0.5 text-[10px] text-neutral-600">
                                    {t}
                                  </span>
                                ))}
                              </div>
                            )}
                            {(c.desire || c.fear || c.flaw) && (
                              <div className="mt-1.5 space-y-0.5 text-[10px] text-neutral-500">
                                {c.desire && <p><span className="text-neutral-400">渴望：</span>{c.desire}</p>}
                                {c.fear && <p><span className="text-neutral-400">恐惧：</span>{c.fear}</p>}
                                {c.flaw && <p><span className="text-neutral-400">缺陷：</span>{c.flaw}</p>}
                              </div>
                            )}
                            {c.speakingStyle && (
                              <p className="mt-1 text-[10px] text-neutral-400">
                                <span className="text-neutral-400">说话风格：</span>{c.speakingStyle}
                              </p>
                            )}
                            <p className="mt-1 text-[10px] text-neutral-500">{c.relation}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.openingScene15s && (
                    <div className="text-xs text-neutral-500">
                      <span className="font-medium">开场 15s：</span>
                      {result.openingScene15s}
                    </div>
                  )}

                  {result.storyline && (
                    <div className="text-xs">
                      <div className="mb-2 font-medium text-neutral-600">故事大纲</div>
                      <div className="space-y-2">
                        {result.storyline
                          .split(/\n{1,}/)
                          .map((para) => para.trim())
                          .filter(Boolean)
                          .map((para, i) => (
                            <p key={i} className="leading-relaxed text-neutral-600">
                              {para}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-1">
                  <Button variant="agent" agentColor="radar" onClick={handleConfirm} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                    {saving ? '保存中…' : '加入主榜'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
